/**
 * CXP-local model for the redesigned Client-Request-List (CRL) detail page.
 *
 * Deliberately owned by the CXP feature (NOT lib-gql-api) so the new page can
 * iterate on its shape independently of the legacy `ClientRequestDetails`
 * fragment and the generated GraphQL client. `ClientRequestServiceApiBridge`
 * maps today's `GetClientRequestDetails` query into this shape; when the real
 * backend endpoints land, only the bridge changes — consumers keep this model.
 */

/** Normalized item status for the new CRL design. */
export enum CrlItemStatus {
  WAITING = 'WAITING',
  IN_REVIEW = 'IN_REVIEW',
  NEEDS_UPDATE = 'NEEDS_UPDATE',
  FINISHED = 'FINISHED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  OVERDUE = 'OVERDUE',
}

/**
 * Whether the client-facing "In Review" concept is surfaced on the CRL.
 *
 * To the client, submitting an item is finishing it: once it is with the firm
 * there is nothing further for them to do, so an intermediate state only adds a
 * step they never act on. While this is `false` the raw `IN_REVIEW` status
 * displays as {@link CrlItemStatus.FINISHED}, the "In Review" metric tile does
 * not render, and the bucket is absent from the filter menu.
 *
 * Flipping this to `true` is the whole of surfacing it again — the status enum,
 * `CRL_STATUS_LABELS`, the status-icon map, the request-level roll-up branch and
 * the filter label/status entries all keep their full shape either way (same
 * convention as `CRL_BULK_ACTIONS_ENABLED`).
 *
 * Undo "I'm Finished" does not read this flag. It is gated on
 * {@link CrlRequestItem.isAwaitingReview}, which is derived from the raw status
 * and so behaves the same in both modes.
 *
 * Typed as `boolean` rather than the inferred `false` literal so the gated
 * branches stay type-checked instead of narrowing to unreachable code.
 */
export const CRL_IN_REVIEW_ENABLED: boolean = false;

/**
 * The display status a client-submitted (raw `IN_REVIEW`) item resolves to.
 * Single source of truth shared by the read mapping in the bridge and the
 * optimistic "I'm Finished" patch in the state, so the two can never drift.
 */
export const CRL_SUBMITTED_ITEM_STATUS: CrlItemStatus = CRL_IN_REVIEW_ENABLED ? CrlItemStatus.IN_REVIEW : CrlItemStatus.FINISHED;

/**
 * A tab of the item-detail overlay. Used to ask the panel to open on a
 * particular tab — the row's comment control opens straight onto the thread.
 */
export type CrlPanelTab = 'documents' | 'comments' | 'activity';

/** Item kind, mirrored from the legacy `RequestItemType`. */
export enum CrlItemType {
  QUESTIONNAIRE = 'QUESTIONNAIRE',
  DOCUMENT_REQUEST = 'DOCUMENT_REQUEST',
  E_SIGN = 'E_SIGN',
}

/** A file attached to a single request item (files nested under items). */
export interface CrlItemFile {
  id: number;
  fileId: number;
  name: string;
  isFlagged: boolean;
  createdAt: string | null;
  /** File size in bytes; null when the payload did not carry `fileInfo.size`. */
  size: number | null;
  /** DMS external file id (`external_dms_file_id`); drives the DMS previewer. Null when absent. */
  dmsId: string | null;
  /** True for firm-provided reference files ("From your firm"); false for client uploads. */
  isReference: boolean;
}

/** A person assigned to a request item. */
/**
 * Who an assignee record is: a client contact, or a secure-link guest invited
 * by email with item-scoped access. Both render as people; the distinction
 * matters for identity (`contactId` vs `secureLinkId`) and future revocation.
 */
export type CrlAssigneeKind = 'contact' | 'secureLink';

export interface CrlAssignee {
  kind: CrlAssigneeKind;
  /** Assignee row id — null for secure-link guests. */
  id: number | null;
  /** Null for secure-link guests. */
  contactId: number | null;
  name: string | null;
  email: string | null;
  /** Client owner — starred and listed first. Never true for a guest. */
  isOwner: boolean;
  /** The secure_link id — the revoke handle; null for contacts. */
  secureLinkId: number | null;
}

/** A single request item, with its files nested directly underneath. */
export interface CrlRequestItem {
  id: number;
  name: string;
  description: string;
  ordinal: number;
  type: CrlItemType;
  /** Normalized status (already mapped from the raw enum). */
  status: CrlItemStatus;
  /** Human label for `status`, resolved via `CRL_STATUS_LABELS`. */
  statusLabel: string;
  /**
   * True when the item's RAW backend (database) status permits client
   * modification — open (`OPEN`/`TO_DO`, which is where a past-due "overdue" item
   * still sits) or changes-requested (`REJECTED`). False once the item is
   * `IN_REVIEW` or `DONE`. A pure status check keyed on the raw DB status (not the
   * derived display `status` above), computed in the bridge so this model stays
   * API-agnostic. Optional: absent is treated as false (fail closed) until the
   * bridge resolves it.
   */
  isEditableStatus?: boolean;
  /**
   * True when the item's RAW backend status is `IN_REVIEW` — the client has
   * submitted it but the firm has not accepted it yet, so "I'm Finished" can
   * still be undone.
   *
   * This is what tells submitted and firm-accepted items apart while
   * {@link CRL_IN_REVIEW_ENABLED} maps both onto the display status `FINISHED` —
   * the distinction Undo depends on, since undoing an item the firm has already
   * accepted is not the client's to make. Keyed on the raw status like
   * {@link isEditableStatus} and resolved in the bridge for the same reason, so
   * it reads the same whether or not the In Review concept is surfaced.
   * Optional: absent is treated as false (fail closed) until the bridge
   * resolves it.
   */
  isAwaitingReview?: boolean;
  /**
   * True when the item's RAW backend status is `DONE` — the firm has accepted
   * it, and nothing the client does can take it back.
   *
   * Distinct from the display status, which reads `FINISHED` for a submitted
   * item too, and from `isAwaitingReview`, which is false in one other case that
   * must stay undoable: an item annotated N/A whose submission call failed, so
   * it is `isNotApplicable` while its raw status is still open. Keyed on the raw
   * status and resolved in the bridge, like the two flags above. Optional:
   * absent is treated as false, which keeps undo offered rather than stranding
   * an item — the server refuses an illegal transition regardless.
   */
  isAccepted?: boolean;
  dueDate: string | null;
  isReadyToSubmit: boolean;
  isNotApplicable: boolean;
  notApplicableReason: string | null;
  /**
   * The firm's per-item flag/rejection reason (legacy `RequestItem.notes`,
   * rendered by the legacy panel as the "Changes Requested" reason). Shown once
   * as the Documents-tab flag banner when the item is `NEEDS_UPDATE`. Per-item
   * (same for all its files), null when the firm left no message.
   */
  flagMessage: string | null;
  /**
   * True when the item is access-restricted to specific recipients (legacy
   * `RequestItem.restricted`). Drives the lock indicator on the row and excludes
   * the item from bulk-selection. Optional: absent (treated as false) until the
   * bridge resolves it.
   */
  isRestricted?: boolean;
  /**
   * Saved questionnaire answer, resolved with the legacy masked-aware
   * precedence (isMasked → masked marker; decrypted answer; raw metadata
   * answer; ''). Empty string for document items.
   */
  answer?: string;
  /** True when the answer is masked for this caller — render read-only. */
  isAnswerMasked?: boolean;
  assignees: CrlAssignee[];
  /**
   * Number of files attached to the item, from the `/page` read. The files
   * themselves load lazily (`/item/detail`) when the overlay opens, so the row
   * shows the count without the whole file tree being fetched up front.
   */
  fileCount: number;
  files: CrlItemFile[];
  /**
   * True when the item carries at least one comment. Absent on the `/page` read —
   * it is joined on from the comments live query by
   * `CxpCrlSubscriptionsStateSelectors.getDisplaySectionsWithComments`, so an item
   * taken straight from `detailData` legitimately has it undefined.
   */
  hasComments?: boolean;
  /** True when a staff comment on the item has not been viewed by this caller. Same source as {@link hasComments}. */
  hasUnviewedComments?: boolean;
}

/** A section grouping request items. */
export interface CrlRequestSection {
  id: number;
  name: string;
  ordinal: number;
  items: CrlRequestItem[];
}

/**
 * Deterministic pastel palette key for an activity actor's avatar. Resolved from
 * the actor id/name so the same person always gets the same colour. Each key has
 * a matching `.crl-activity-avatar--<key>` rule (pastel background + darker text)
 * in the activity component SCSS.
 */
export type CrlAvatarColorKey = 'teal' | 'blue' | 'purple' | 'amber' | 'rose' | 'green' | 'indigo' | 'slate';

/**
 * What a linked object in the action line represents, driving its colour (Figma).
 * Colour is INDEPENDENT of file chips (`CrlActivityEntry.files`): e.g. a flagged
 * file is a teal link with NO chip, a removed file is a muted link with no chip.
 * - `item` — an item name ("accepted {item}") OR a person name
 *   ("changed the reviewer to {name}", "reassigned the request to {name}")
 *   → plain dark text, NOT teal.
 * - `file` — a live file name (single upload / replacement's new file / flagged)
 *   → teal link.
 * - `file-deleted` — a removed file name → muted, no link colour.
 */
export type CrlActivityLinkKind = 'item' | 'file' | 'file-deleted';

/**
 * A file chip on an activity entry (Figma: filename + size + download icon).
 * Sourced from the endpoint's `referenceFiles` (structured) or, defensively,
 * from a `data.file_list` group. Carries what the previewer/downloader needs.
 */
export interface CrlActivityFile {
  /** Numeric file id (drives the previewer/downloader). */
  id: number;
  /** DMS external id (`externalId`); the previewer's `dmsFileId`. Null when absent. */
  externalId: string | null;
  /** Display file name. */
  name: string;
  /** File size in bytes; null when the payload omitted it. */
  size: number | null;
}

/**
 * A comment/reason box on an activity entry. `isCritical` renders the red
 * "requested changes" box (Figma: bg red-1100 / border red-800 / text red-100);
 * otherwise a normal white-bordered box (comments, N/A notes).
 */
export interface CrlActivityComment {
  text: string;
  isCritical: boolean;
}

/**
 * A single activity-feed entry, mapped from the anduin per-item activity
 * endpoint (`GET …/client-request/section/item/{id}/activity`). Fully composed
 * for the presentational feed — the component renders these directly and never
 * re-derives phrasing, colours, or initials.
 *
 * The client feed uses PRODUCT phrasing, never status language (e.g. REJECTED →
 * "requested changes"). Fields degrade gracefully: `referenceFiles` is empty
 * today and `data` is loosely typed, so `files`/`comment`/`linkedObject` are all
 * optional.
 */
export interface CrlActivityEntry {
  /** Stable key for `@for` tracking (composed from index + timestamp). */
  id: string;
  /** Raw `EventOperation` name (e.g. `SENT`, `APPROVED`). Kept for future affordances. */
  eventType: string;
  /** Display name for the actor ("You" when self, else fullName/name). */
  actorName: string;
  /** 1-2 letter initials for the avatar. */
  actorInitials: string;
  /** Deterministic pastel palette key for the avatar. */
  avatarColorKey: CrlAvatarColorKey;
  /** Action phrase that follows the actor name (e.g. "sent", "requested changes"). */
  action: string;
  /** Optional linked object name rendered after the action (e.g. the item or file name). */
  linkedObject: string | null;
  /** What `linkedObject` represents — drives its colour (item = plain dark, file = teal). */
  linkedObjectKind: CrlActivityLinkKind | null;
  /** File chips. Empty when none (the norm today — `referenceFiles` is unpopulated). */
  files: CrlActivityFile[];
  /** Optional comment/reason box. Null when none. */
  comment: CrlActivityComment | null;
  /** ISO timestamp string for the relative-time pipe. Null when absent. */
  timestamp: string | null;
  /** Best-effort: true when performed by the current (client) user. */
  isSelf: boolean;
  /**
   * The backend masked this row's content (restricted item): names and bodies
   * arrive as `"......"` and file ids are null, so chips can never resolve a
   * preview/download — the feed renders them inert.
   */
  isMasked: boolean;
}

/** The request's firm reviewer, sourced from the `/page` read. */
export interface CrlRequestReviewer {
  staffId: number;
  name: string;
  /** Resolved on the `/page` read (backend joins the staff row); null when absent. */
  email: string | null;
  isOwner: boolean;
}

/** The request's service line, sourced from the `/page` read. */
export interface CrlRequestServiceLine {
  id: number;
  name: string;
}

/** A request-level client-access person (contact or secure-link guest), sourced from the `/page` read. */
export interface CrlRequestAssignee {
  kind: CrlAssigneeKind;
  /** Null for secure-link guests. */
  contactId: number | null;
  fullName: string;
  /** Resolved on the `/page` read (backend joins contactInfo); null when absent. */
  email: string | null;
  /** True for a contact assigned at the request level — the client owner. Never true for a secure-link guest. */
  isOwner: boolean;
  /** The secure_link id — the revoke handle; null for contacts. */
  secureLinkId: number | null;
}

/**
 * The full detail payload the new CRL page renders. `status` is the new
 * normalized string; `statusLabel` is its display label. Activity is NOT part of
 * this payload — it is fetched per-item from a dedicated endpoint and lives in
 * its own state slice.
 *
 * The request-header fields (name/description/clientName/serviceLine/reviewers/
 * assignees/nextDueDate) come from the `/page` read so the CRL-page selector
 * reads them directly, without joining against the tasks state.
 */
export interface CrlRequestDetail {
  id: number;
  clientId: number;
  name: string;
  description: string;
  clientName: string | null;
  jobId: number | null;
  serviceLine: CrlRequestServiceLine | null;
  reviewers: CrlRequestReviewer[];
  assignees: CrlRequestAssignee[];
  nextDueDate: string | null;
  status: CrlItemStatus;
  statusLabel: string;
  submittedAt: string | null;
  sections: CrlRequestSection[];
}
